/**
 * Step 4: 청킹 전략 설정
 */

import React, { useMemo } from 'react';
import { ChunkingConfig } from '../../../api';
import { SourceType, CHUNK_STRATEGIES } from '../shared/pipeline.types';
import { SourceTabs } from '../shared/SourceTabs';
import { Badge } from '../../ui';
import CheckIcon from '../../../assets/icons/common/check.svg?react';

// ── 유효성 검사 함수 ──
export interface ChunkingValidation {
  chunkSize?: { min: number; max: number };
  chunkOverlap?: { min: number; max: number };
  parentChunkSize?: { min: number; max: number };
  childChunkSize?: { min: number; max: number };
}

export function validateChunking(config: ChunkingConfig, source: SourceType): { isValid: boolean; errors: Record<string, boolean> } {
  const errors: Record<string, boolean> = {};
  const isDb = source === 'database';
  const isWeb = source === 'web';

  // 입력 필요 없는 전략
  if (config.strategy === 'default' || config.strategy === 'semantic' || config.strategy === 'none') {
    return { isValid: true, errors };
  }

  // DB 필드 조합 / 고정 길이
  if (isDb && (config.strategy === 'paragraph' || config.strategy === 'fixed')) {
    const size = config.chunkSize;
    if (size === undefined || size === null || isNaN(size) || size < 100 || size > 2000) {
      errors.chunkSize = true;
    }
  }

  // 웹 문단 단위
  if (isWeb && config.strategy === 'paragraph') {
    const size = config.chunkSize;
    const overlap = config.chunkOverlap;
    if (size === undefined || size === null || isNaN(size) || size < 100 || size > 2000) {
      errors.chunkSize = true;
    }
    if (overlap === undefined || overlap === null || isNaN(overlap) || overlap < 0 || overlap > 200) {
      errors.chunkOverlap = true;
    }
  }

  // 파일/웹 고정 크기
  if (!isDb && config.strategy === 'fixed') {
    const size = config.chunkSize;
    const overlap = config.chunkOverlap;
    if (size === undefined || size === null || isNaN(size) || size < 100 || size > 2000) {
      errors.chunkSize = true;
    }
    if (overlap === undefined || overlap === null || isNaN(overlap) || overlap < 0 || overlap > 500) {
      errors.chunkOverlap = true;
    }
  }

  // 계층형
  if (config.strategy === 'hierarchical') {
    const parent = config.parentChunkSize ?? 0;
    const child = config.childChunkSize ?? 0;
    const overlap = config.chunkOverlap;
    if (parent < 500 || parent > 8000 || isNaN(parent)) {
      errors.parentChunkSize = true;
    }
    if (child < 100 || child > 2000 || isNaN(child)) {
      errors.childChunkSize = true;
    }
    if (overlap === undefined || overlap === null || isNaN(overlap) || overlap < 0 || overlap > 500) {
      errors.chunkOverlap = true;
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}
import DefaultIcon from '../../../assets/icons/chunking/default.svg?react';
import FixedIcon from '../../../assets/icons/chunking/fixed.svg?react';
import HierarchicalIcon from '../../../assets/icons/chunking/hierarchical.svg?react';
import SemanticIcon from '../../../assets/icons/chunking/semantic.svg?react';
import NoneIcon from '../../../assets/icons/chunking/none.svg?react';
// 분할 미리보기 그림 (컬러 SVG → ?react 쓰면 단색이 되므로 <img> 로 사용)
import chunkingDefaultImg from '../../../assets/illustrations/chunking-default.svg';
import chunkingFixedImg from '../../../assets/illustrations/chunking-fixed.svg';
import chunkingHierarchicalImg from '../../../assets/illustrations/chunking-hierarchical.svg';
import chunkingSemanticImg from '../../../assets/illustrations/chunking-semantic.svg';
import chunkingNoneImg from '../../../assets/illustrations/chunking-none.svg';
import DbFieldsIcon from '../../../assets/icons/chunking/db-fields.svg?react';
import chunkingDbFieldsImg from '../../../assets/illustrations/chunking-db-fields.svg';
import chunkingDbFixedImg from '../../../assets/illustrations/chunking-db-fixed.svg';
import chunkingDefaultWebImg from '../../../assets/illustrations/chunking-web-default.svg';
import chunkingWebParagraphImg from '../../../assets/illustrations/chunking-web-paragraph.svg';
import { isAdminRole } from '../../../utils/role';
import { authApi } from '../../../api';
// 사용자 화면용 파일 청킹 그림 (사용자 테마 색)
import userChunkingDefaultImg from '../../../assets/illustrations/user-chunking-default.svg';
import userChunkingFixedImg from '../../../assets/illustrations/user-chunking-fixed.svg';
import userChunkingHierarchicalImg from '../../../assets/illustrations/user-chunking-hierarchical.svg';
import userChunkingSemanticImg from '../../../assets/illustrations/user-chunking-semantic.svg';
import userChunkingNoneImg from '../../../assets/illustrations/user-chunking-none.svg';
import userChunkingWebDefaultImg from '../../../assets/illustrations/user-chunking-web-default.svg';
import userChunkingWebParagraphImg from '../../../assets/illustrations/user-chunking-web-paragraph.svg';

// ── 청킹 화면 (타일 + 설정 패널) 표시용 정보 - 파일 · 웹 · DB 소스 ──
// 타일 이름 · 설명은 CHUNK_STRATEGIES[소스] 그대로, 여기는 아이콘 · 미리보기 · 오른쪽 제목 아래 문구만
type SvgIcon = React.FC<React.SVGProps<SVGSVGElement>>;
type TileSource = SourceType;
interface ChunkUi {
  icon: SvgIcon;
  image: string;
  imageAlt: string;
  previewDesc: string;
  chips: string[];
  subtitle: string;
}

// DB 소스 (필드 조합 · 고정 길이) - 입력은 청크 크기 1개 (DB 는 오버랩 없음)
const DB_CHUNK_UI: Partial<Record<ChunkingConfig['strategy'], ChunkUi>> = {
  paragraph: {
    icon: DbFieldsIcon,
    image: chunkingDbFieldsImg,
    imageAlt: '제목 · 본문 등 여러 컬럼을 합쳐 청크를 만들고, 최대 크기를 넘는 부분은 나누는 그림',
    previewDesc: '여러 컬럼을 합쳐 하나의 청크로 만들고, 최대 크기를 넘으면 나눕니다.',
    chips: ['제목 · 본문이 나뉜 테이블'],
    subtitle: '청크 하나의 최대 크기를 지정합니다.',
  },
  fixed: {
    icon: FixedIcon,
    image: chunkingDbFixedImg,
    imageAlt: '테이블마다 스키마 문서를 만들고, 같은 토큰 수로 나누는 그림',
    previewDesc: '테이블마다 스키마 문서를 만들고, 같은 토큰 수로 나눕니다.',
    chips: ['컬럼이 많은 테이블', '긴 텍스트 컬럼'],
    subtitle: '청크 크기를 지정합니다.',
  },
};

const FILE_CHUNK_UI: Partial<Record<ChunkingConfig['strategy'], ChunkUi>> = {
  default: {
    icon: DefaultIcon,
    image: chunkingDefaultImg,
    imageAlt: '약 300토큰 크기 청크를 20%(60토큰)씩 겹쳐 나누는 그림',
    previewDesc: '비슷한 크기로 나누고, 앞뒤를 조금씩 겹칩니다.',
    chips: ['대부분의 문서'],
    subtitle: '설정할 항목이 없습니다.',
  },
  fixed: {
    icon: FixedIcon,
    image: chunkingFixedImg,
    imageAlt: '입력한 청크 크기로 나누고, 입력한 오버랩 토큰 수만큼 앞뒤 청크를 겹치는 그림',
    previewDesc: '입력한 크기로 똑같이 자르고, 입력한 토큰 수만큼 겹칩니다.',
    chips: ['엑셀', '표형 데이터'],
    subtitle: '청크 크기와 오버랩을 직접 지정합니다.',
  },
  hierarchical: {
    icon: HierarchicalIcon,
    image: chunkingHierarchicalImg,
    imageAlt: '부모 청크를 작은 자식 청크로 나눠, 자식 청크로 검색하고 부모 청크를 문맥으로 전달하는 그림',
    previewDesc: '작은 자식 청크로 찾고, 그 자식이 속한 부모 청크를 답변 문맥으로 넘깁니다.',
    chips: ['긴 문서', '소설', '논문', '매뉴얼'],
    subtitle: '부모·자식 청크 크기와 오버랩을 지정합니다.',
  },
  semantic: {
    icon: SemanticIcon,
    image: chunkingSemanticImg,
    imageAlt: '문장 의미가 바뀌는 지점에서 나눠 청크마다 크기가 다른 그림',
    previewDesc: '주제가 바뀌는 지점에서 나누므로 청크마다 크기가 다릅니다.',
    chips: ['정확도가 가장 중요한 문서'],
    subtitle: '설정할 항목이 없습니다.',
  },
  none: {
    icon: NoneIcon,
    image: chunkingNoneImg,
    imageAlt: '문서를 나누지 않고 문서 전체를 청크 1개로 처리하는 그림',
    previewDesc: '문서 1개가 그대로 청크 1개가 됩니다.',
    chips: ['이미 짧게 나뉜 문서'],
    subtitle: '설정할 항목이 없습니다.',
  },
};

// 오른쪽 값 요약표 (입력창 없는 전략)
const FILE_CHUNK_SUMMARY: Partial<Record<ChunkingConfig['strategy'], Array<[string, string]>>> = {
  default:  [['청크 크기', '약 300토큰'], ['오버랩', '20%'], ['분할 기준', '문장 경계 존중']],
  semantic: [['분할 기준', '의미 단위 (AI 분석)'], ['청크 크기', 'AI가 자동 결정'], ['처리', '느림 · 비용 발생']],
  none:     [['청크 크기', '문서 전체'], ['오버랩', '없음'], ['분할 기준', '분할하지 않음']],
};

// 웹 소스 (기본값 · 고정 크기 · 문단 단위)
// 기본값 = 웹 표준 약 500토큰 · 오버랩 20% (DEFAULT_WEB_CHUNKING 500/100, 백엔드에서 고정 크기로 처리)
const WEB_CHUNK_UI: Partial<Record<ChunkingConfig['strategy'], ChunkUi>> = {
  default: {
    icon: DefaultIcon,
    image: chunkingDefaultWebImg,
    imageAlt: '약 500토큰 크기 청크를 20%씩 겹쳐 페이지를 나누는 그림',
    previewDesc: '페이지를 비슷한 크기로 나누고, 앞뒤를 조금씩 겹칩니다.',
    chips: ['대부분의 사이트'],
    subtitle: '설정할 항목이 없습니다.',
  },
  fixed: {
    icon: FixedIcon,
    image: chunkingFixedImg,
    imageAlt: '입력한 청크 크기로 나누고, 입력한 오버랩 토큰 수만큼 앞뒤 청크를 겹치는 그림',
    previewDesc: '입력한 크기로 똑같이 자르고, 입력한 토큰 수만큼 겹칩니다.',
    chips: ['기술 문서', '긴 본문 페이지'],
    subtitle: '청크 크기와 오버랩을 직접 지정합니다.',
  },
  paragraph: {
    icon: DbFieldsIcon,
    image: chunkingWebParagraphImg,
    imageAlt: '빈 줄을 경계로 문단마다 청크 1개를 만드는 그림',
    previewDesc: '빈 줄을 경계로 문단마다 청크를 만들고, 최대 크기를 넘으면 나눕니다.',
    chips: ['블로그', '뉴스 기사'],
    subtitle: '최대 청크 크기와 오버랩을 지정합니다.',
  },
};

const CHUNK_UI: Record<TileSource, Partial<Record<ChunkingConfig['strategy'], ChunkUi>>> = {
  file: FILE_CHUNK_UI,
  web: WEB_CHUNK_UI,
  database: DB_CHUNK_UI,
};

// 사용자(로그인 역할 기준)일 때 청킹 미리보기 그림 - 소스별, 없는 전략은 기본(관리자) 그림
const USER_CHUNK_IMAGES: Record<TileSource, Partial<Record<ChunkingConfig['strategy'], string>>> = {
  file: {
    default:      userChunkingDefaultImg,
    fixed:        userChunkingFixedImg,
    hierarchical: userChunkingHierarchicalImg,
    semantic:     userChunkingSemanticImg,
    none:         userChunkingNoneImg,
  },
  web: {
    default:   userChunkingWebDefaultImg,
    fixed:     userChunkingFixedImg, // 관리자 화면과 같이 파일 고정 크기 그림 공용
    paragraph: userChunkingWebParagraphImg,
  },
  database: {},
};

// 오른쪽 값 요약표 (입력창 없는 전략) - 소스별
const CHUNK_SUMMARY: Record<TileSource, Partial<Record<ChunkingConfig['strategy'], Array<[string, string]>>>> = {
  file: FILE_CHUNK_SUMMARY,
  web: {
    default: [['청크 크기', '약 500토큰'], ['오버랩', '20%'], ['분할 기준', '줄바꿈 · 단어 경계']],
  },
  database: {},
};

// ── 파일 · DB 소스: 전략 타일 + 선택된 전략 설정 패널 ──
// 저장 값 · 입력 처리(min/max · 빈 값 대체)는 기존 ChunkingDetail 과 동일
function ChunkingTileSelect({
  source,
  config,
  onChange,
}: {
  source: TileSource;
  config: ChunkingConfig;
  onChange: React.Dispatch<React.SetStateAction<ChunkingConfig>>;
}) {
  const strategies = CHUNK_STRATEGIES[source];
  const uiMap = CHUNK_UI[source];
  const tileCount = strategies.length;
  const selectedIndex = Math.max(0, strategies.findIndex((s) => s.value === config.strategy));
  const selected = strategies[selectedIndex];
  const ui = selected ? uiMap[selected.value] : undefined;
  // 미리보기 그림 - 사용자면 소스별 사용자 테마 그림 (소스 선택 단계와 같은 로그인 역할 기준)
  const isAdmin = isAdminRole(authApi.getUser()?.role);
  const previewImage =
    !isAdmin && selected
      ? USER_CHUNK_IMAGES[source][selected.value] ?? ui?.image
      : ui?.image;
  const summary = selected ? CHUNK_SUMMARY[source][selected.value] : undefined;
  const isDb = source === 'database';
  const isWeb = source === 'web';

  // 유효성 검사
  const { errors } = useMemo(() => validateChunking(config, source), [config, source]);
  const getInputClass = (field: string) => 
    `pipe-settings-input${errors[field] ? ' has-error' : ''}`;

  return (
    <div className="chunk-b2">
      {/* 전략 타일 (라디오 그룹) - 칸 수 = 전략 수 */}
      <div
        className="chunk-b2-tiles"
        role="radiogroup"
        aria-label="청킹 전략"
        style={{ '--tile-count': tileCount, '--tile-count-sm': Math.min(3, tileCount) } as React.CSSProperties}
      >
        {strategies.map(({ value, title, desc }) => {
          const isSelected = config.strategy === value;
          const Icon = uiMap[value]?.icon;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`chunk-b2-tile${isSelected ? ' selected' : ''}`}
              onClick={() => onChange((prev) => ({ ...prev, strategy: value }))}
            >
              {isSelected && <span className="chunk-b2-check" aria-hidden="true"><CheckIcon /></span>}
              {Icon && <span className="chunk-b2-tile-icon"><Icon /></span>}
              <span className="chunk-b2-tile-title">{title}</span>
              <span className="chunk-b2-tile-desc">{desc}</span>
            </button>
          );
        })}
      </div>

      {/* 설정 패널 - 꼬리 위치 = 선택된 타일 가운데 (타일 수 만큼 칸, 간격 12px) */}
      {selected && ui && (
        <div
          className="chunk-b2-panel"
          style={{ '--tail-left': `calc((100% - ${(tileCount - 1) * 12}px) / ${tileCount} * ${selectedIndex + 0.5} + ${selectedIndex * 12}px)` } as React.CSSProperties}
        >
          {/* 왼쪽: 미리보기 카드 */}
          <div className="chunk-b2-preview">
            <div className="chunk-b2-preview-label">분할 미리보기</div>
            <img className="chunk-b2-preview-img" src={previewImage} alt={ui.imageAlt} />
            <p className="chunk-b2-preview-desc">{ui.previewDesc}</p>
            <hr className="chunk-b2-divider" />
            <div className='chunk-b2-fit-label-wrap'>
              <div className="chunk-b2-fit-label mb-8">적합한 문서</div>
              <div className="chunk-b2-chips">
                {ui.chips.map((c) => <Badge key={c} variant="type" shape="pill">{c}</Badge>)}
              </div>
            </div>
          </div>

          {/* 오른쪽: 설정 내용 */}
          <div className="chunk-b2-detail">
            <div className="chunk-b2-detail-head">
              <h4 className="chunk-b2-detail-title">{selected.title}</h4>
              <p className="chunk-b2-detail-sub">{ui.subtitle}</p>
            </div>

            {/* 입력창 없는 전략: 값 요약표 + 안내문 */}
            {summary && (
              <dl className="chunk-b2-summary">
                {summary.map(([label, val]) => (
                  <div key={label} className="chunk-b2-summary-row">
                    <dt>{label}</dt>
                    <dd>{val}</dd>
                  </div>
                ))}
              </dl>
            )}

            {!isWeb && config.strategy === 'default' && (
              <div className="chunk-b2-guide">
                <strong>기본값</strong>은 AWS 권장 설정으로 자동 처리합니다. 약 <strong>300토큰</strong> 크기,
                <strong> 20% 오버랩</strong>, 문장 경계를 존중하여 분할합니다. 별도 설정이 필요 없습니다.
              </div>
            )}

            {/* 웹 기본값 - 웹 표준 (카드 설명 · 그림과 같은 500토큰 · 20%) */}
            {isWeb && config.strategy === 'default' && (
              <div className="chunk-b2-guide">
                <strong>기본값</strong>은 웹 표준 설정으로 자동 처리합니다. 약 <strong>500토큰</strong> 크기,
                <strong> 20% 오버랩</strong>으로 페이지를 분할합니다. 별도 설정이 필요 없습니다.
              </div>
            )}

            {config.strategy === 'semantic' && (
              <div className="chunk-b2-guide">
                <strong>의미 기반 청킹</strong>은 AI가 문서의 의미를 분석하여 자동으로 최적의 분할 지점을
                찾습니다. 처리 시간이 더 오래 걸리고 비용이 발생할 수 있습니다.
              </div>
            )}

            {config.strategy === 'none' && (
              <div className="chunk-b2-guide">
                <strong>청킹 안 함</strong>은 문서 전체를 하나의 청크로 처리합니다. 문서가 임베딩 모델의
                최대 토큰을 초과하면 색인이 실패할 수 있으니, 짧게 나뉜 문서에만 사용하세요.
              </div>
            )}

            {/* DB 필드 조합: 최대 청크 크기 1개 (DB 는 오버랩 없음) */}
            {isDb && config.strategy === 'paragraph' && (
              <div className="chunk-b2-fields">
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">최대 청크 크기 (토큰) <span className="field-range">100~2000</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('chunkSize')}
                    value={config.chunkSize ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, chunkSize: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    청크가 이 크기를 초과하면 분할됩니다. 문단이 짧은 콘텐츠는 <strong>300~500</strong>, 긴 문단이 많은 문서는 <strong>600~1000</strong>을 권장합니다.
                  </div>
                </div>
              </div>
            )}

            {/* DB 고정 길이: 청크 크기 1개 (DB 는 오버랩 없음) */}
            {isDb && config.strategy === 'fixed' && (
              <div className="chunk-b2-fields">
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">청크 크기 (토큰) <span className="field-range">100~2000</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('chunkSize')}
                    value={config.chunkSize ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, chunkSize: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    테이블마다 스키마 문서가 별도로 생성됩니다. <strong>선택한 테이블 중 컬럼이 가장 많은 테이블</strong>을 기준으로 설정하세요. 컬럼 10개 이하는 <strong>500</strong>, 30개 이상은 <strong>1000~1500</strong>을 권장합니다.
                  </div>
                </div>
              </div>
            )}

            {/* 웹 문단 단위: 최대 청크 크기 + 오버랩 (기존 ChunkingDetail 웹 문단 분기와 동일) */}
            {isWeb && config.strategy === 'paragraph' && (
              <div className="chunk-b2-fields">
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">최대 청크 크기 (토큰) <span className="field-range">100~2000</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('chunkSize')}
                    value={config.chunkSize ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, chunkSize: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    청크가 이 크기를 초과하면 분할됩니다. 문단이 짧은 콘텐츠는 <strong>300~500</strong>, 긴 문단이 많은 문서는 <strong>600~1000</strong>을 권장합니다.
                  </div>
                </div>
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">오버랩 (토큰) <span className="field-range">0~200</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('chunkOverlap')}
                    value={config.chunkOverlap ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, chunkOverlap: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    인접한 문단 간에 공유할 토큰 수입니다.<br />
                    <strong>소설·논문·매뉴얼</strong>처럼 앞뒤 문맥이 중요한 문서는 <strong>20~50</strong>을 권장합니다. 표·레코드처럼 각 행이 독립된 데이터는 <strong>0</strong>을 권장합니다.
                  </div>
                </div>
              </div>
            )}

            {/* 파일 · 웹 고정 크기: 입력 2개 (2열) - 청크 크기 도움말만 소스별 */}
            {!isDb && config.strategy === 'fixed' && (
              <div className="chunk-b2-fields">
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">청크 크기 (토큰) <span className="field-range">100~2000</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('chunkSize')}
                    value={config.chunkSize ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, chunkSize: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    {isWeb
                      ? <>웹 페이지를 나누는 단위입니다. 짧은 본문 페이지는 <strong>300~500</strong>, 긴 기술 문서 페이지는 <strong>800~1200</strong>을 권장합니다.</>
                      : <>문서를 나누는 단위입니다. 짧은 FAQ·뉴스는 <strong>200~400</strong>, 논문·보고서는 <strong>500~800</strong>, 긴 매뉴얼은 <strong>800~1200</strong>을 권장합니다.</>}
                  </div>
                </div>
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">오버랩 (토큰) <span className="field-range">0~500</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('chunkOverlap')}
                    value={config.chunkOverlap ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, chunkOverlap: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    인접한 청크 간에 공유할 토큰 수입니다.<br />
                    <strong>소설·논문·매뉴얼</strong>처럼 앞뒤 문맥이 중요한 문서는 <strong>50~100</strong>을 권장합니다. 표·레코드처럼 각 행이 독립된 데이터는 <strong>0</strong>을 권장합니다.
                  </div>
                </div>
              </div>
            )}

            {/* 계층형: 입력 3개 (부모 · 자식 / 오버랩) */}
            {config.strategy === 'hierarchical' && (
              <div className="chunk-b2-fields">
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">부모 청크 크기 (토큰) <span className="field-range">500~8000</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('parentChunkSize')}
                    value={config.parentChunkSize ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, parentChunkSize: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    검색 시 문맥으로 제공되는 넓은 단위입니다. 자식 크기의 3~5배를 권장합니다 (예: 자식 300 → 부모 1000~1500).
                  </div>
                </div>
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">자식 청크 크기 (토큰) <span className="field-range">100~2000</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('childChunkSize')}
                    value={config.childChunkSize ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, childChunkSize: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    실제 검색에 사용되는 정밀한 단위입니다. 짧고 구체적인 답변이 필요하면 <strong>200~400</strong>, 문맥이 넓어야 하면 <strong>400~600</strong>을 권장합니다.
                  </div>
                </div>
                <div className="pipe-settings-field">
                  <label className="pipe-settings-label">오버랩 (토큰) <span className="field-range">0~500</span></label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={getInputClass('chunkOverlap')}
                    value={config.chunkOverlap ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      onChange((prev) => ({ ...prev, chunkOverlap: val === '' ? undefined as any : parseInt(val) }));
                    }}
                  />
                  <div className="chunk-hint-text">
                    자식 청크 간에 공유할 토큰 수입니다.<br />
                    <strong>소설·논문·매뉴얼</strong>처럼 앞뒤 문맥이 중요한 문서는 <strong>50~100</strong>을 권장합니다. 표·레코드처럼 각 행이 독립된 데이터는 <strong>0</strong>을 권장합니다.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface ChunkingStepProps {
  activeSources: SourceType[];
  activeTab: SourceType;
  onActiveTabChange: (tab: SourceType) => void;
  fileChunking: ChunkingConfig;
  setFileChunking: React.Dispatch<React.SetStateAction<ChunkingConfig>>;
  webChunking: ChunkingConfig;
  setWebChunking: React.Dispatch<React.SetStateAction<ChunkingConfig>>;
  dbChunking: ChunkingConfig;
  setDbChunking: React.Dispatch<React.SetStateAction<ChunkingConfig>>;
}

export function ChunkingStep({
  activeSources, activeTab, onActiveTabChange,
  fileChunking, setFileChunking,
  webChunking,  setWebChunking,
  dbChunking,   setDbChunking,
}: ChunkingStepProps) {
  const configMap: Record<SourceType, ChunkingConfig> = {
    file: fileChunking, web: webChunking, database: dbChunking,
  };
  const setterMap: Record<SourceType, React.Dispatch<React.SetStateAction<ChunkingConfig>>> = {
    file: setFileChunking, web: setWebChunking, database: setDbChunking,
  };

  React.useEffect(() => {
    if (dbChunking.chunkOverlap !== 0) {
      setDbChunking(prev => ({ ...prev, chunkOverlap: 0 }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const config = configMap[activeTab];
  const setter = setterMap[activeTab];

  return (
    <div className="form-item--md pipe-section">
      <div className="form-label">
        청킹 전략
        <div className='optional'>
          수집된 텍스트를 검색 가능한 단위로 분할하는 방법을 선택하세요.
        </div>
      </div>
      <SourceTabs sources={activeSources} active={activeTab} onChange={onActiveTabChange} />
      {/* 파일 · 웹 · DB 소스: 타일 + 설정 패널 (B-2) */}
      <div className="pipe-tab-content">
        <ChunkingTileSelect source={activeTab} config={config} onChange={setter} />
      </div>
    </div>
  );
}
